import type React from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import { type TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { TOUR_CALENDAR_MODES, useAvailabilityDates, useTourCalendarApi } from '@zg-rentals/touring';
import type { AvailabilityDate, AvailabilityEvent, TimeZoneIds } from '@zg-rentals/touring';
import { useQueryClient } from '@zg-rentals/react-query5';

import { useSelector } from '../../../../selectors/useSelector';
import { STEP } from '../../../../containers/CreateListingPage/constants';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';

export interface TourCalendarApiType {
  availabilityEvents: Array<AvailabilityEvent>;
  tourConfirmationType?: TourConfirmationType;
  timeZone?: TimeZoneIds;
  enableTouringForListing: (method: TourConfirmationType, zone: TimeZoneIds) => Promise<unknown>;
  setAvailabilitiesLite: (availabilityDto: unknown) => Promise<unknown>;
  isEnableTouringForListingLoading: boolean;
  isGetTourConfigAndAvailabilityLoading: boolean;
  [key: string]: unknown;
}

export interface TourSchedulingContextType {
  selectedBookingMethod?: TourConfirmationType;
  selectedTimeZone?: TimeZoneIds;
  availabilityDates: Array<AvailabilityDate>;
  canAccessPostingPathTourScheduling: boolean;

  setSelectedBookingMethod: (method: TourConfirmationType | undefined) => void;
  setSelectedTimeZone: (zone: TimeZoneIds | undefined) => void;
  setAvailabilityDates: (dates: Array<AvailabilityDate>) => void;

  tourCalendarApiHook: ReturnType<typeof useTourCalendarApi>;
}

export const TourSchedulingContext = createContext<TourSchedulingContextType | undefined>(undefined);

interface TourSchedulingProviderProps {
  children: React.ReactNode;
  initialState?: {
    selectedTimeZone?: TimeZoneIds;
    availabilityDates?: Array<AvailabilityDate>;
  };
  currentStepKey: string;
  canAccessPostingPathTourScheduling: boolean;
  selectedBookingMethod: TourConfirmationType | undefined;
  setSelectedBookingMethod: (method: TourConfirmationType | undefined) => void;
}

export const TourSchedulingProvider: React.FC<TourSchedulingProviderProps> = ({
  children,
  initialState,
  currentStepKey,
  canAccessPostingPathTourScheduling,
  selectedBookingMethod,
  setSelectedBookingMethod,
}) => {
  const queryClient = useQueryClient();
  const [selectedTimeZone, setSelectedTimeZone] = useState<TimeZoneIds | undefined>(initialState?.selectedTimeZone);
  const [availabilityDates, setAvailabilityDates] = useState<Array<AvailabilityDate>>(
    initialState?.availabilityDates || [],
  );
  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const isOnTourSchedulingStep = [
    STEP.TOUR_SCHEDULING_BOOKING_METHOD,
    STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
    STEP.TOUR_SCHEDULING_TIMEZONE,
    STEP.TOUR_SCHEDULING_TIMEZONE_RELIST,
    STEP.TOUR_SCHEDULING_AVAILABILITY,
    STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    STEP.REVIEW,
  ].find((step) => step === currentStepKey);

  const onTouringToggleSuccess = useCallback(
    (isDisabling?: boolean) => {
      if (isDisabling) {
        setSelectedBookingMethod(undefined);
        setSelectedTimeZone(undefined);
      }
      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
    },
    [queryClient, setSelectedBookingMethod, setSelectedTimeZone],
  );

  const tourCalendarApiHook = useTourCalendarApi({
    enableQueries: Boolean(canAccessPostingPathTourScheduling && listingAlias && isOnTourSchedulingStep),
    listingAlias,
    mode: TOUR_CALENDAR_MODES.TOUR_AVAILABILITY,
    onTouringToggleSuccess,
  });

  const [currentAvailabilityDates] = useAvailabilityDates(tourCalendarApiHook.availabilityEvents);

  useEffect(() => {
    setSelectedBookingMethod(tourCalendarApiHook.tourConfirmationType);
  }, [setSelectedBookingMethod, tourCalendarApiHook.tourConfirmationType]);

  useEffect(() => {
    setSelectedTimeZone(tourCalendarApiHook.timeZone);
  }, [setSelectedTimeZone, tourCalendarApiHook.timeZone]);

  useEffect(() => {
    setAvailabilityDates(currentAvailabilityDates);
  }, [currentAvailabilityDates, setAvailabilityDates]);

  const contextValue: TourSchedulingContextType = {
    // Shared state
    selectedBookingMethod,
    selectedTimeZone,
    availabilityDates,
    canAccessPostingPathTourScheduling,

    // State setters
    setSelectedBookingMethod,
    setSelectedTimeZone,
    setAvailabilityDates,

    // Tour Calendar API object
    tourCalendarApiHook,
  };

  return <TourSchedulingContext.Provider value={contextValue}>{children}</TourSchedulingContext.Provider>;
};
